import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline"
import PubSub from "pubsub-js"
import MenuGroup from "aldoo-ra/MenuGroup"
import DynamicSVG from "aldoo-ra/DynamicSVG"
import SiteLanguageSelector from "../SiteLanguageSelector/button"
import Localization from "aldoo-ra/Localization"
import Icon from "aldoo-ra/Icon"
import Button from "aldoo-ra/Button"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import Typography from "aldoo-ra/Typography"
import { navigate } from "vike/client/router"
import useSitePlatform from "@components/Site/useSitePlatform"

const HamburgerMenu = ({ isOpen, setIsOpen }) => {
  const [platform, setPlatform] = useSitePlatform()
  const { template } = Localization()

  const toggleMenu = (event) => {
    event.stopPropagation()
    setIsOpen(!isOpen)
  }

  const closeMenu = () => {
    setIsOpen(false)
  }

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      {/* Hamburger Button */}
      <button
        className="p-2 rounded-md text-text hover:text-blue_primary"
        onClick={toggleMenu}
        aria-label="Toggle Menu"
      >
        {isOpen ? (
          <XMarkIcon className="w-6 h-6" />
        ) : (
          <Bars3Icon className="w-6 h-6" />
        )}
      </button>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="fixed bottom-[80px] inset-x-0 top-0 z-40 bg-white px-2 pt-[90px] flex flex-col gap-4 overflow-y-auto" style={{ boxShadow: 'inset 0 -10px 10px -10px rgba(0, 0, 0, 0.15)' }}>
          {/* Menu Groups */}
          <div className="flex flex-col gap-10 px-4">
            <MenuGroup
              name="Header Products"
              renderAs="panel"
              variant="aqua-panel-menu-v-products"
              showMenuMode="click"
              containMenu
              onItemClick={item=>{
                closeMenu()

                // Parse the target URL to get the platform
                const targetPath = item.link[0].url
                const targetSegments = targetPath.split('/').filter(Boolean)
                // The platform is always the first segment (android, ios, windows)
                const urlPlatform = targetSegments[0]

                let currentUrl = window.location.href
                const pathname = window.location.pathname

                // Check if URL has language code
                const langMatch = pathname.match(/^\/([a-z]{2})(\/|$)/)
                const langPrefix = langMatch ? `/${langMatch[1]}` : ''

                //also set the platform from the link[0].url
                setPlatform(urlPlatform)
                //if the user is a at a feature page, redirect to the feature page
                //redirect to the same page for the current platform
                if (pathname.includes("features")) {
                  // Get the path segment after language (if present) or first segment
                  const pathSegments = pathname.split('/').filter(Boolean)
                  const platformIndex = langMatch ? 1 : 0
                  const currentPlatform = pathSegments[platformIndex]

                  //replace the current platform with the new platform
                  const newUrl = currentUrl.replace(
                    `/${currentPlatform}`,
                    `/${urlPlatform}`
                  )

                  //special case for advanced-features not available on ios
                  //redirect to account-and-setup instead
                  if (
                    urlPlatform === "ios" &&
                    pathname.includes("advanced-features")
                  ) {
                    const redirectUrl = newUrl.replace("advanced-features", "account-and-setup")
                    navigate(redirectUrl.replace(window.location.origin, ''))
                    return
                  }

                  //navigate to the new url
                  navigate(newUrl.replace(window.location.origin, ''))
                  return
                }
                //just navigate to the link[0].url when not in a feature page
                // Prepend language prefix to the target URL if needed
                const targetUrl = langPrefix ? `${langPrefix}${item.link[0].url}` : item.link[0].url
                navigate(targetUrl)
              }}

              rootItemRender={(item) => {
                return (
                  <Button
                    variant="nav_menuitem"
                    className="flex items-center justify-center gap-[5px] !text-[20px] px-0 lg:mr-10 xl:mr-[68px]"
                  >
                    {item.label}

                    <Icon id={platform} className="w-5 h-5 mb-1" />

                    <div className="w-5 h-5 flex items-center justify-center">
                      <ChevronDownIcon className="w-4 h-4" strokeWidth={3} />
                    </div>
                  </Button>
                )
              }}
            />

            <MenuGroup
              name="Header Features"
              renderAs="panel"
              variant="aqua-panel-menu-v"
              showMenuMode="click"
              containMenu
              onItemClick={closeMenu}
              preProcessItem={(item) => {
                if (item.items) {
                  item.items = item.items
                    .filter((item) => {
                      const { platforms } = item.options || {}
                      if (!platforms) return true
                      return platforms.toLowerCase().includes(platform)
                    })
                    .map((i) => {
                      if (i.link?.[0]?.url) {
                        i.link[0].url = template(i.link[0].url, { platform })
                      }
                      return i
                    })
                }
                return item
              }}
            />

            <MenuGroup
              name="Header Pricing"
              renderAs="hlist"
              variant="aqua-panel-menu-v-item"
              onItemClick={closeMenu}
            />

            <MenuGroup
              name="Header Partners"
              renderAs="panel"
              variant="aqua-panel-menu-v-lowercase"
              showMenuMode="click"
              containMenu
              onItemClick={closeMenu}
            />

            <MenuGroup
              name="Header Resources"
              renderAs="panel"
              variant="aqua-panel-menu-v-lowercase"
              showMenuMode="click"
              containMenu
              onItemClick={closeMenu}
            />
          </div>

          {/* Account and Language Selector */}
          <div className="mt-20 ml-1 mb-8">
            <MenuGroup
              name="Header Profile"
              renderAs="hlist.icons"
              actionMap={{
                signin: () =>
                  PubSub.publish("signin", {
                    redirect: "/account",
                  }),
              }}
              onItemClick={closeMenu}
              menuItemRender={(item) => (
                <DynamicSVG
                  url={item.image}
                  className="w-6 h-6 text-primary hover:text-primary_hov ml-2 mb-6 transition"
                />
              )}
            />

            <SiteLanguageSelector
              menuPosition="top"
              showMenuMode="click"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default HamburgerMenu